<template>
  <!-- MLB-Style Privacy Policy Page -->
  <div class="bg-white dark:bg-slate-900 min-h-screen">
    
    <PageHeader
      title="Privacy Policy"
      subtitle="Your privacy is important to us"
      centered
    />

    <!-- Content Section -->
    <div class="max-w-4xl mx-auto px-4 py-12">
      <div class="bg-white dark:bg-slate-800 rounded-lg shadow border border-gray-200 dark:border-slate-700 p-8">
        
        <!-- Last Updated -->
        <div class="mb-8 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700">
          <p class="text-sm text-blue-800 dark:text-blue-200">
            <span class="font-semibold">Last Updated:</span> {{ lastUpdated }}
          </p>
        </div>

        <!-- Introduction -->
        <section class="mb-8">
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">Introduction</h2>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed mb-4">
            Welcome to Softball Stats Club ("we," "our," or "us"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our softball statistics tracking application and website.
          </p>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed">
            By using our service, you agree to the collection and use of information in accordance with this Privacy Policy.
          </p>
        </section>

        <!-- Information We Collect -->
        <section class="mb-8">
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">Information We Collect</h2>
          
          <h3 class="text-xl font-semibold text-gray-900 dark:text-slate-200 mb-3">Personal Information</h3>
          <ul class="list-disc list-inside text-gray-700 dark:text-slate-300 mb-4 space-y-2">
            <li>Name and contact information</li>
            <li>Email address</li>
            <li>Account credentials</li>
            <li>Team affiliations and player statistics</li>
            <li>Profile information from Google OAuth (if you choose to sign in with Google)</li>
          </ul>

          <h3 class="text-xl font-semibold text-gray-900 dark:text-slate-200 mb-3">Usage Information</h3>
          <ul class="list-disc list-inside text-gray-700 dark:text-slate-300 mb-4 space-y-2">
            <li>Log data and usage patterns</li>
            <li>Device information and browser type</li>
            <li>IP address and location data</li>
            <li>Cookies and similar tracking technologies</li>
          </ul>
        </section>

        <!-- How We Use Information -->
        <section class="mb-8">
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">How We Use Your Information</h2>
          <ul class="list-disc list-inside text-gray-700 dark:text-slate-300 space-y-2">
            <li>Provide and maintain our softball statistics service</li>
            <li>Process user registrations and manage accounts</li>
            <li>Track and display player and team statistics</li>
            <li>Communicate with you about your account and our services</li>
            <li>Improve our application and user experience</li>
            <li>Ensure security and prevent fraud</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <!-- Google OAuth -->
        <section class="mb-8">
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">Google OAuth Integration</h2>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed mb-4">
            When you choose to sign in with Google, we receive limited information from your Google account including:
          </p>
          <ul class="list-disc list-inside text-gray-700 dark:text-slate-300 mb-4 space-y-2">
            <li>Your name and email address</li>
            <li>Profile picture (if available)</li>
            <li>Google account ID for authentication purposes</li>
          </ul>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed">
            This information is used solely for account creation and authentication. We do not access any other Google services or data.
          </p>
        </section>

        <!-- Information Sharing -->
        <section class="mb-8">
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">Information Sharing</h2>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed mb-4">
            We do not sell, trade, or otherwise transfer your personal information to third parties except in the following circumstances:
          </p>
          <ul class="list-disc list-inside text-gray-700 dark:text-slate-300 space-y-2">
            <li>With your explicit consent</li>
            <li>To comply with legal requirements</li>
            <li>To protect our rights and safety</li>
            <li>In connection with a business transfer or merger</li>
          </ul>
        </section>

        <!-- Data Security -->
        <section class="mb-8">
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">Data Security</h2>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed">
            We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure.
          </p>
        </section>

        <!-- Your Rights -->
        <section class="mb-8">
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">Your Rights</h2>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed mb-4">
            You have the right to:
          </p>
          <ul class="list-disc list-inside text-gray-700 dark:text-slate-300 space-y-2">
            <li>Access and update your personal information</li>
            <li>Request deletion of your account and data</li>
            <li>Opt-out of certain communications</li>
            <li>Request a copy of your data</li>
          </ul>
        </section>

        <!-- Contact Information -->
        <section class="mb-8">
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">Contact Us</h2>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed mb-4">
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </p>
          <div class="bg-gray-50 dark:bg-slate-700 p-4 rounded-lg">
            <p class="text-gray-700 dark:text-slate-300">
              <strong>Email:</strong> privacy@sdsapps.com<br>
              <strong>Website:</strong> https://softball.sdsapps.com
            </p>
          </div>
        </section>

        <!-- Changes to Policy -->
        <section>
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">Changes to This Privacy Policy</h2>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed">
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued use of our service after any changes constitutes acceptance of the new Privacy Policy.
          </p>
        </section>

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import PageHeader from '@/components/common/PageHeader.vue'

// Last updated date
const lastUpdated = ref('January 8, 2025')
</script>